/**
 * Provider API Client
 *
 * Service for interacting with the Provider API backend to extract
 * medical codes (HPO, ICD-10-CM) from clinical text using PhenoML's NLP.
 */

import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import * as Sentry from '@sentry/react'
import { withHpoLookupDedupe } from './hpoLookupCache'
import { extractHPOId } from './hpoTypes'

/** Thrown when `/terminology/construe/hpo` returns a non-OK status. */
export class HpoExtractionError extends Error {
  readonly status: number

  constructor(status: number) {
    super('HPO extraction failed')
    this.name = 'HpoExtractionError'
    this.status = status
  }
}

/**
 * A source-span citation for an extracted code: the verbatim span of the
 * submitted text that produced it, with character offsets. provider-api manages
 * the HPO extraction config and always includes citations.
 */
export interface HPOCitation {
  text: string
  begin_offset: number
  end_offset: number
}

export interface HPOCode {
  code: string
  description: string
  /** HPO primary label; `description` may be the matched synonym. */
  label?: string | null
  long_description?: string | null
  rationale?: string | null
  reason?: string | null
  citations?: HPOCitation[] | null
  /** Set only when the request asked for `annotate`: whether the note negates the finding. */
  assertion?: 'present' | 'absent'
  /** Set only with `annotate`: whose finding it is. Prenatal findings may be the fetus's or the mother's. */
  subject?: 'patient' | 'prenatal' | 'maternal' | 'family'
  /** Set only with `annotate`: `low-confidence` when the cited text barely matches the term. */
  flags?: string[]
}

export interface ICD10Code {
  code: string
  description: string
  long_description?: string | null
  rationale?: string | null
  reason?: string | null
}

/**
 * provider-api manages the HPO extraction config and rejects one from the client, so for
 * `construeHPO` only `hpoVersion` and `annotate` apply.
 */
export interface ConstrueOptions {
  /**
   * Method for dividing text into chunks
   * Options: 'sentence', 'paragraph', 'topics', 'none'
   * Default: 'sentence'
   */
  chunkingMethod?: 'sentence' | 'paragraph' | 'topics' | 'none'

  /**
   * Maximum number of codes to return per chunk
   * Range: 1-50
   * Default: 10
   */
  maxCodesPerChunk?: number

  /**
   * Minimum similarity threshold for filtering duplicate codes
   * Range: 0.0-1.0 (higher = more strict filtering)
   * Default: 0.7
   */
  codeSimilarityFilter?: number

  /**
   * Include AI-generated rationale for each code
   * Default: false
   */
  includeRationale?: boolean

  /**
   * Include per-code source citations (verbatim span + character offsets of the
   * submitted text that produced each code).
   */
  includeCitations?: boolean

  /**
   * Optional HPO release version to extract against, forwarded to provider-api
   * as the PhenoML builtin `system.version` (e.g. '2024-08-13'). When omitted,
   * the server default HPO version is used. Used to pin extraction to a
   * lab-compatible HPO build so submitted codes are mappable by the lab.
   */
  hpoVersion?: string

  /**
   * HPO only. Return negated, relatives' and prenatal findings labelled with `assertion`, `subject`
   * and `flags` instead of dropping them.
   */
  annotate?: boolean
}

/**
 * Lab-compatible HPO builtin version (GEN-3392). Tertiary vendors (Rady CAT,
 * SeqOne) run older HPO releases and reject terms minted after their build
 * (observed: HP:4000064 "Poor sleep"). Pin order extraction to this builtin so
 * submitted codes are mappable by the lab. Maps to obophenotype release
 * v2024-08-13.
 */
export const LAB_HPO_VERSION = '2024-08-13'

/**
 * Snake_case config payload forwarded to provider-api's
 * `/terminology/construe/{hpo,icd10}` routes.
 *
 * Only fields the caller explicitly customizes are included; missing
 * fields are filled in by the server-side `HPO_DEFAULT_CONFIG` via merge
 * semantics in provider-api's `_resolve_hpo_config` (GEN-2521 / GEN-2566).
 */
interface ConstrueRequestBodyConfig {
  chunking_method?: string
  code_similarity_filter?: number
  include_rationale?: boolean
  include_citations?: boolean
  max_codes_per_chunk?: number
}

interface ConstrueRequestBody {
  text: string
  config?: ConstrueRequestBodyConfig
  hpo_version?: string
  annotate?: boolean
}

/**
 * Build the request body for `/terminology/construe/{hpo,icd10}`.
 *
 * Forwards ONLY the fields the caller explicitly set. Anything left
 * undefined is omitted from the body so the server can apply its own
 * default. We deliberately do NOT hard-code legacy SDK-9 defaults like
 * `chunking_method: 'sentence'` or `code_similarity_filter: 0.7` here:
 * those silently overrode the server's selected SDK-10.3 chunking +
 * validator config and were the root cause of the dev-portal HPO
 * extraction symptoms reported during UAT (GEN-T42 / GEN-2521 / GEN-2566).
 */
function buildConstrueRequestBody(text: string, options?: ConstrueOptions): ConstrueRequestBody {
  const config: ConstrueRequestBodyConfig = {}
  if (options?.chunkingMethod !== undefined) config.chunking_method = options.chunkingMethod
  if (options?.codeSimilarityFilter !== undefined) config.code_similarity_filter = options.codeSimilarityFilter
  if (options?.includeRationale !== undefined) config.include_rationale = options.includeRationale
  if (options?.includeCitations !== undefined) config.include_citations = options.includeCitations
  if (options?.maxCodesPerChunk !== undefined) config.max_codes_per_chunk = options.maxCodesPerChunk

  // Omit `config` entirely when the caller passed nothing -- the server
  // route will see `request.config is None` and apply HPO_DEFAULT_CONFIG.
  const body: ConstrueRequestBody = Object.keys(config).length > 0 ? { text, config } : { text }

  // GEN-3392: forward an explicit HPO version (e.g. '2024-08-13') when the
  // caller pins one; omitted leaves the server default untouched.
  if (options?.hpoVersion !== undefined) body.hpo_version = options.hpoVersion
  if (options?.annotate !== undefined) body.annotate = options.annotate

  return body
}

/**
 * Provider API Client for medical code extraction
 */
class HPOAPIClient {
  providerAPIClient: ProviderAPIClient

  constructor() {
    this.providerAPIClient = ProviderAPIClient.getInstance()
  }

  /**
   * Extract HPO (Human Phenotype Ontology) codes from clinical text
   *
   * @param text Clinical text to analyze
   * @param options Optional tuning parameters
   * @returns Array of HPO codes with descriptions
   *
   * @example
   * ```typescript
   * const codes = await hpoAPIClient.construeHPO(
   *   "Patient presents with seizures and developmental delay",
   *   { includeRationale: true }
   * )
   * ```
   */
  async construeHPO(text: string, options?: ConstrueOptions, signal?: AbortSignal): Promise<HPOCode[]> {
    // Forward only fields the caller explicitly customized; the server-side
    // HPO_DEFAULT_CONFIG (clinical_ner_extract + chunk_code_jaccard_similarity
    // + threshold 0.25, per the SDK 10.3 bump in GEN-1979 / GEN-2427) fills
    // in everything else via merge semantics in provider-api's
    // `_resolve_hpo_config` (see GEN-2521 / GEN-2566). Hard-coding stale
    // SDK-9 defaults like `chunking_method: 'sentence'` here was silently
    // overriding the server's selected chunking + validator config and is
    // what caused the dev-portal HPO-extraction symptoms during UAT.
    const requestBody = buildConstrueRequestBody(text, options)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    const response = await this.providerAPIClient.post('/terminology/construe/hpo', requestBody, headers, true, signal)

    if (!response.ok) {
      const hpoError = new HpoExtractionError(response.status)
      Sentry.captureException(hpoError, {
        tags: { operation: 'hpo.extract' },
        contexts: { request: { options }, response: { status: response.status } },
      })
      throw hpoError
    }

    return response.json()
  }

  /**
   * Extract ICD-10-CM codes from clinical text
   *
   * @param text Clinical text to analyze
   * @param options Optional tuning parameters
   * @returns Array of ICD-10-CM codes with descriptions
   *
   * @example
   * ```typescript
   * const codes = await hpoAPIClient.construeICD10(
   *   "Patient diagnosed with type 2 diabetes mellitus",
   *   { maxCodesPerChunk: 5 }
   * )
   * ```
   */
  async construeICD10(text: string, options?: ConstrueOptions, signal?: AbortSignal): Promise<ICD10Code[]> {
    // See note in construeHPO: same merge-semantics contract.
    const requestBody = buildConstrueRequestBody(text, options)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    const response = await this.providerAPIClient.post(
      '/terminology/construe/icd10',
      requestBody,
      headers,
      true,
      signal
    )

    if (!response.ok) {
      const icd10Error = new Error('ICD-10 extraction failed')
      Sentry.captureException(icd10Error, {
        tags: { operation: 'icd10.extract' },
        contexts: { request: { options }, response: { status: response.status } },
      })
      throw icd10Error
    }

    return response.json()
  }

  async searchHPO(query: string, limit: number = 20, signal?: AbortSignal): Promise<HPOCode[]> {
    const params = new URLSearchParams({ text: query, limit: String(limit) })
    const url = `/terminology/search/hpo?${params.toString()}`
    const response = await this.providerAPIClient.get(
      url,
      {
        'Content-Type': 'application/json',
      },
      true,
      signal
    )

    if (!response.ok) {
      const hpoError = new Error('HPO search failed')
      Sentry.captureException(hpoError, {
        tags: { operation: 'hpo.search' },
        contexts: { request: { limit }, response: { status: response.status } },
      })
      throw hpoError
    }
    return response.json()
  }

  /**
   * Resolve an HPO term by ID using the search API.
   * Returns { id, lbl } in canonical form only when an exact match is found; otherwise null.
   * Does not fall back to the first search result, since text search can return unrelated terms.
   */
  async getHPOTermById(id: string, signal?: AbortSignal): Promise<{ id: string; lbl: string } | null> {
    if (!id?.trim()) return null
    const normalizedId = extractHPOId(id)
    return withHpoLookupDedupe(normalizedId, async () => {
      const results = await this.searchHPO(normalizedId, 5, signal)
      const match = results.find((r) => extractHPOId(r.code) === normalizedId)
      if (!match) return null
      return { id: extractHPOId(match.code), lbl: match.description }
    })
  }
}

// Lazy singleton instance
let _instance: HPOAPIClient | null = null

/**
 * Get the singleton instance of ProviderAPIClient
 * Lazily instantiated on first use to avoid module import failures
 */
function getHPOAPIClient(): HPOAPIClient {
  if (!_instance) {
    _instance = new HPOAPIClient()
  }
  return _instance
}

// Export singleton getter with convenient API
export const hpoAPIClient = {
  construeHPO: (text: string, options?: ConstrueOptions, signal?: AbortSignal) =>
    getHPOAPIClient().construeHPO(text, options, signal),
  construeICD10: (text: string, options?: ConstrueOptions, signal?: AbortSignal) =>
    getHPOAPIClient().construeICD10(text, options, signal),
  searchHPO: (query: string, limit?: number, signal?: AbortSignal) =>
    getHPOAPIClient().searchHPO(query, limit ?? 20, signal),
  getHPOTermById: (id: string, signal?: AbortSignal) => getHPOAPIClient().getHPOTermById(id, signal),
}
